

import {
  computed,
  defineComponent, onMounted, PropType, Ref, ref,
} from 'vue';
import {
  ICase, ICreateFolder, IError, IFolder, IMedia, IMixedData, IMixedSelectedItem,
} from '@/types/interfaces';
import {
  FOLDER_TYPES, MEDIA_TYPES, MIXED_DATA_TYPE, SHARE_TYPES,
} from '@/types/enums';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import MoveNavigation from './MoveNavigation.vue';
import MoveControls from './MoveControls.vue';
import MoveBody from './MoveBody.vue';
import AppSmallLoader from '@/components/UI/AppSmallLoader.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
import DriveService from '@/modules/drive/services/DriveService';
import CreateFolder from './CreateFolder.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import NotificationService from '@/services/NotificationService';
import SkeletonService from '@/services/SkeletonService';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';

export default defineComponent({
  components: {
    AppBottomPopUp,
    MoveNavigation,
    MoveControls,
    MoveBody,
    AppSmallLoader,
    AppLoader,
    CreateFolder,
  },
  props: {
    item: { type: Object as PropType<IMedia | IFolder | ICase>, required: false },
    itemType: { type: Number as PropType<SHARE_TYPES>, required: false },
    manualMove: { type: Boolean, required: false },
    mixedItems: { type: Object as () => IMixedSelectedItem[], required: false },
  },
  emits: ['close', 'updateLocation', 'move'],
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const mixedList: Ref<IMixedData[] | null> = ref(null);
    const loader: Ref<boolean> = ref(false);

    const bigLoader: Ref<string> = ref('');
    const mainStore = useMainStore();

    // const pageIdx: Ref<number> = ref(0);
    // const lastPage: Ref<number> = ref(1);
    // const scroller = ref();
    // const loadMore: Ref<boolean> = ref(false);

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage,
    } = UsePagination(450);

    const root: Ref<boolean> = ref(true);
    const rootData: Ref<IMixedData[] | undefined> = ref(undefined);
    const currentFolder: Ref<IFolder | undefined> = ref(undefined);

    const isCreateFolder: Ref<boolean> = ref(false);
    const createFolderError: Ref<string> = ref('');

    function cancelCreateFolder() {
      isCreateFolder.value = false;
    }

    function openCreateFolder() {
      isCreateFolder.value = true;
    }

    function createFolder(folderName: string) {
      driveStore.createFolder(folderName, currentFolder.value?.id)
        .then((response) => {
          const folder = response as IFolder;
          isCreateFolder.value = false;
          const mix: IMixedData = {
            id: folder.id,
            type: MIXED_DATA_TYPE.FOLDER,
            folder,
          };

          mixedList.value = [mix, ...mixedList.value!];
        })
        .catch((e) => {
          if (e?.response?.data?.errorKey === 'FOLDER_NAME_EXISTS') {
            createFolderError.value = 'Name is already taken';
          } else {
            createFolderError.value = 'Something gone wrong';
          }
        });
    }

    const isUpperFolderRoot = computed(() => {
      if (root.value || !rootData.value || !currentFolder.value) {
        return false;
      }

      return !!rootData.value.find((mix) => mix.id === currentFolder.value?.id);
    });

    const showDots = computed(() => {
      if (!isUpperFolderRoot.value && !root.value) {
        return true;
      }
      return false;
    });

    const currentFolderText = computed(() => {
      if (root.value || !currentFolder.value) {
        return '';
      }
      return currentFolder.value.name;
    });

    const disableMove = computed(() => {
      if (props.item) {
        return !!mixedList.value?.find((item) => item.id === props.item?.id);
      }
      if (props.mixedItems) {
        return !!props.mixedItems?.find((mix) => mix.folderId === currentFolder.value?.id);
      }
      return false;
    });

    function hidePopUp() {
      emit('close');
    }

    function cancel() {
      hidePopUp();
    }

    function clearCachedItem(itemId: string): void {
      if (driveStore.currentCase) {
        driveStore.currentCase.photos = driveStore.currentCase.photos.filter((photo) => photo.id !== itemId);
        driveStore.currentCase.files = driveStore.currentCase.files.filter((file) => file.id !== itemId);
      }
      if (driveStore.currentFolder?.mixedData) {
        driveStore.currentFolder.mixedData = driveStore.currentFolder.mixedData.filter((mix) => mix.id !== itemId);
      }
      if (currentFolder.value?.id) {
        driveStore.mixedData.data = driveStore.mixedData.data.filter((mix) => mix.id !== itemId);
      }
      // driveStore.photos.data = driveStore.photos.data.filter((photo) => photo.id !== itemId);
      // driveStore.files = driveStore.files.filter((file) => file.id !== itemId);
      // driveStore.photoList = driveStore.photoList.filter((photo) => photo.id !== itemId);
    }

    async function resolveMoveAction() {
      if (props.itemType === SHARE_TYPES.CASE) {
        bigLoader.value = 'Moving case...';
        return DriveService.moveCase(props.item!.id!, currentFolder.value?.id || null);
      }
      if (props.itemType === SHARE_TYPES.FOLDER) {
        bigLoader.value = 'Moving folder...';
        return DriveService.moveFolder(props.item!.id!, currentFolder.value?.id || null);
      }
      bigLoader.value = props.itemType === SHARE_TYPES.FILE ? 'Moving file...' : 'Moving photo...';
      return DriveService.moveFile(props.item!.id!, currentFolder.value?.id || null);
    }

    function setSkeletons() {
      if (currentFolder.value?.id) {
        SkeletonService.setFolderSkeletons(currentFolder.value?.id, 1, MEDIA_TYPES.FILES);
      } else {
        SkeletonService.setMixedSkeletons(1);
      }
    }

    function removeSkeletons() {
      if (currentFolder.value?.id) {
        SkeletonService.removeFolderSkeletons(currentFolder.value?.id, 1, MEDIA_TYPES.FILES);
      } else {
        SkeletonService.removeMixedSkeletons(1);
      }
    }

    function toMixedData(): IMixedData {
      if (props.itemType === SHARE_TYPES.FILE || props.itemType === SHARE_TYPES.PHOTO) {
        return {
          id: props.item!.id!,
          type: MIXED_DATA_TYPE.FILE,
          file: props.item as IMedia,
        };
      }
      if (props.itemType === SHARE_TYPES.FOLDER) {
        return {
          id: props.item!.id!,
          type: MIXED_DATA_TYPE.FOLDER,
          folder: props.item as IFolder,
        };
      }
      return {
        id: props.item!.id!,
        type: MIXED_DATA_TYPE.CASE,
        case: props.item as ICase,
      };
    }

    function syncData(folderId: string | undefined) {
      const isCurrentFolder = folderId && driveStore.currentFolder?.id === folderId;
      const mixItem = toMixedData();
      if (isCurrentFolder) {
        const hasItem = driveStore.currentFolder?.mixedData?.find((mix) => mix.id === props.item?.id);
        if (hasItem) {
          return;
        }
        driveStore.currentFolder!.mixedData = [mixItem, ...driveStore.currentFolder!.mixedData!];
      }

      if (!folderId) {
        driveStore.mixedData.data = [mixItem, ...driveStore.mixedData.data];
      }
    }

    function moveTo() {
      if (props.manualMove) {
        emit('move', currentFolder.value?.id);
        emit('close');
        return;
      }
      setSkeletons();
      resolveMoveAction()
        .then(() => {
          syncData(currentFolder.value?.id);
          emit('updateLocation', currentFolder.value?.id || null);
          NotificationService.notifySuccess();
        })
        .finally(() => { removeSkeletons(); });
      clearCachedItem(props.item!.id!);
      bigLoader.value = '';
      emit('close');
    }

    async function fetchMixedData() {
      return DriveService.getMixedData(pageIdx.value, '')
        .then((response) => {
          if (response.success) {
            const mixed = response.results.data.items.filter((mix) => !mix.deleted);
            if (pageIdx.value > 0) {
              mixedList.value!.push(...mixed);
            } else {
              mixedList.value = mixed;
            }

            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          }
          return response;
        })
        .finally(() => { loader.value = false; });
    }

    async function fetchFolder(folderId: string) {
      if (pageIdx.value > 0) {
        return DriveService.getMixedData(pageIdx.value, '', folderId).then((response) => {
          const mixed = response.results.data.items.filter((mix) => !mix.deleted);
          mixedList.value!.push(...mixed);

          return response;
        });
      }
      return Promise.all([DriveService.getFolderById(folderId), DriveService.getMixedData(pageIdx.value, '', folderId)]).then((response) => {
        if (response[0].success) {
          currentFolder.value = response[0].results.data;
          if (response[1].success) {
            const mixed = response[1].results.data.items.filter((mix) => !mix.deleted);
            mixedList.value = mixed;
            setLastPage(response[1].results.data.paging.items, response[1].results.data.paging.itemsOnPage);
          }
        }

        return response[1];
      });
    }

    function openFolder(folderId: string) {
      root.value = false;
      loader.value = true;
      pageIdx.value = 0;

      fetchFolder(folderId).finally(() => { loader.value = false; });
    }

    function goToDrive() {
      root.value = true;
      loader.value = true;
      pageIdx.value = 0;
      currentFolder.value = undefined;
      fetchMixedData().then((response) => {
        if (response.success) {
          rootData.value = response.results.data.items;
        }
      });
    }

    function goBack() {
      if (currentFolder.value?.upperFolderId) {
        openFolder(currentFolder.value?.upperFolderId);
      } else {
        goToDrive();
      }
    }

    function resolveFolderIdFromProps(): string | null {
      if (props.itemType === SHARE_TYPES.FOLDER) {
        return (props.item as IFolder).upperFolderId;
      }
      if ((props.item as ICase)?.folderId) {
        return (props.item as ICase).folderId;
      }
      return null;
    }

    function fetchData() {
      if (root.value) {
        return fetchMixedData();
      }
      return fetchFolder(currentFolder.value!.id);
    }

    function handleScroll() {
      onScroll(fetchData, DIRECTION.BOTTOM, loader.value);
    }

    onMounted(() => {
      const folderId = resolveFolderIdFromProps();
      if (folderId) {
        openFolder(folderId);
      } else {
        goToDrive();
      }
    });

    return {
      mixedList,
      loader,
      scroller,
      isPageLoading,
      showDots,
      currentFolder,
      currentFolderText,
      bigLoader,
      disableMove,
      isCreateFolder,
      createFolderError,
      hidePopUp,
      goToDrive,
      goBack,
      cancel,
      moveTo,
      createFolder,
      handleScroll,
      openFolder,
      cancelCreateFolder,
      openCreateFolder,
      mainStore,
    };
  },
});
